// react query for getEpics

import { useMutation, useQuery } from '@tanstack/react-query';
import { createEpic, getEpics, updateEpic, updateEpicsOrder } from '../api/workspace';
import { Epic } from '../types/Epic';
import { flushSync } from 'react-dom';
import { queryClient } from '../queryClient';
import { useGetActualSpaceId } from './workspace';
import { QUERY_CONFIG } from '../constants/queryConfig';

export const useEpics = (spaceId: number) => {
  return useQuery<Epic[], Error>({
    queryKey: ['epics', spaceId],
    queryFn: () => getEpics(spaceId),
    enabled: !!spaceId,
    staleTime: QUERY_CONFIG.STALE_TIME,
  });
};

export const useCurrentWorkspaceEpics = () => {
  const spaceId = useGetActualSpaceId();

  return useEpics(Number(spaceId));
};

export const useCreateEpicMutation = () => {
  return useMutation({
    mutationFn: async ({ spaceId, name }: { name: string; spaceId: number }) => {
      const newEpic = await createEpic(spaceId, name);
      return newEpic.data;
    },
  });
};

export const useUpdateEpicMutation = () => {
  return useMutation({
    mutationFn: async ({
      workspaceId,
      epicId,
      epicName,
    }: {
      workspaceId: number;
      epicId: number;
      epicName: string;
    }) => {
      const updatedEpic = await updateEpic(workspaceId, epicId, { title: epicName });

      return updatedEpic.data;
    },
  });
};

export const useUpdateEpicsOrderMutation = () => {
  return useMutation({
    mutationFn: async ({ workspaceId, epicIds }: { workspaceId: number; epicIds: Array<number> }) => {
      await updateEpicsOrder(workspaceId, epicIds);
    },
  });
};

export const useCreateEpic = () => {
  const createEpicMutation = useCreateEpicMutation();

  const createEpic = async (spaceId: number, name: string) => {
    createEpicMutation.mutate(
      { spaceId, name },
      {
        onSuccess: (data) => {
          flushSync(() => {
            queryClient.setQueryData(
              ['epics', spaceId],
              // ✅ this is the way
              (oldData: Epic[]) => [
                ...oldData.filter((epic) => epic.id !== data.id),
                {
                  ...data,
                },
              ]
            );
          });

          setTimeout(() => {
            flushSync(() => {
              // TODO: focus input for epic name
              document.querySelector(`[epic-name-id='${Number(data.id)}']`)?.click();
            });

            const newEpicInput = document.querySelector(`[epic-name-input-id='${Number(data.id)}']`);

            newEpicInput?.focus();
            newEpicInput?.select();
          }, 300);
        },
      }
    );
  };

  return {
    createEpic,
    createEpicMutation,
  };
};
